import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useRef, useState } from 'react'
import type { ChangeEvent, MouseEvent } from 'react'

import { MODALS_ID, PAGE_NAME, QUERY_KEY } from '../../constants'

import { abrStartSubscriptionProduct } from '../../utils/abr'

import { getDepositAccount } from '../../api/depositAccount'
import { getProductDetail } from '../../api/product'
import { getTermsList } from '../../api/terms'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useNavigate } from '../../hooks/useNavigate'
import { useUserInfo } from '../../hooks/useUserInfo'

import { Wrap } from '../../components/container/Wrap'
import { AlignCenterItem, CenterWrap, RowBetween } from '../../components/common'
import { Button } from '../../components/button'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Footer } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { TermsDetailModal } from '../../components/modals/TermsDetailModal'
import { OrderLoadingModal } from './modals/OrderLoadingModal'
import { EventTermsNoticeModal } from './modals/EventTermsNoticeModal'
import {
  StyledContent,
  ProductInfoContainer,
  OrderInfoContainer,
  AmountInputActiveBorder,
  AmountInputContainer,
  AmountInput,
  AmountButtonBox,
  AmountButton,
  CostContainer,
  ResultAmountBox,
  TermsContainer,
  TermsItem,
  EventTerms,
  EventTermsItem,
} from './Order.styled'

import { ReactComponent as CheckedIcon } from '../../assets/ico/common/check/check_checked_blue_fill.svg'
import { ReactComponent as UncheckedIcon } from '../../assets/ico/common/check/check_default_gray_fill.svg'
import { ReactComponent as ArrowIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray-04_medium.svg'

// 최대 청약 금액
const LIMIT_PRICE = 30000000
// 청약 상태
const SUBSCRIPTION_VALIDATE = {
  OKAY: 'OKAY',
  INPUT: 'INPUT',
  OVER_AMOUNT: 'OVER_AMOUNT',
  BALANCE: 'BALANCE',
  TERMS: 'TERMS',
} as const
// 청약 상태에 따른 안내 문구
const SUBSCRIPTION_VALIDATE_MESSAGE = {
  OKAY: '결제하기',
  INPUT: '청약 신청수량을 입력해 주세요',
  OVER_AMOUNT: '최대 신청 가능 금액을 초과했어요',
  BALANCE: '예치금이 부족해요',
  TERMS: '필수 조건에 동의 해주세요',
} as const
// 3호 청약 id
const THIRD_PURCHASE_ID = 62
const THIRD_PURCHASE_EVENT_TERMS_ID_1 = 39 // 3호 청약 이벤트 약관 id 1
const THIRD_PURCHASE_EVENT_TERMS_ID_2 = 40 // 3호 청약 이벤트 약관 id 2

// 251210 3호 청약 테스트
const TEST_THIRD_PURCHASE_ID = 63
const TEST_THIRD_PURCHASE_EVENT_TERMS_ID_1 = 45
const TEST_THIRD_PURCHASE_EVENT_TERMS_ID_2 = 46

type SUBSCRIPTION_VALIDATE_KEY_TYPES = keyof typeof SUBSCRIPTION_VALIDATE

export function Order() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [amountValue, setAmountValue] = useState<number | undefined>() // 청약 신청수량
  const [validateStatus, setValidateStatus] = useState<SUBSCRIPTION_VALIDATE_KEY_TYPES>(SUBSCRIPTION_VALIDATE.INPUT) // footer button status
  const [textWidth, setTextWidth] = useState<number | undefined>() // 청약 신청수량 text width (style)
  const [checkTerms, setCheckTerms] = useState<{ id: number; checked: boolean; required: boolean }[]>([]) // 동의 여부
  const [termsId, setTermsId] = useState<number | undefined>()

  const navigate = useNavigate()

  const productId = Number(location.pathname.split('/')[3])

  const { isLogin, isCheckingLogin } = useUserInfo()

  const {
    data: productDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.product, { productId }],
    queryFn: () => getProductDetail(productId as number),
    enabled: typeof productId === 'number',
  })

  const {
    data: depositAccount,
    isPending: isDepositAccountPending,
    isError: isDepositAccountError,
  } = useQuery({ queryKey: [QUERY_KEY.depositAccount], queryFn: getDepositAccount })

  const {
    data: terms,
    isPending: isTermsPending,
    isError: isTermsError,
  } = useQuery({
    queryKey: [QUERY_KEY.termsList, { page: PAGE_NAME.PURCHASE }],
    queryFn: async () => await getTermsList(PAGE_NAME.PURCHASE),
  })

  const inputTextRef = useRef<HTMLSpanElement | null>(null)

  const balance = depositAccount?.balance ?? 0 // 예치금
  const offerPrice = productDetail?.activeProductSale?.offerPrice ?? 0 // 개당 가격
  const residualDeposit = balance - offerPrice * (amountValue ?? 0) // 잔여 예치금
  const resultAmount = offerPrice * (amountValue ?? 0) // 총 신청 금액

  // 3호 청약 이벤트 선택 약관을 모두 동의했는가
  // TODO: parmaId, termsId 바꿔줘야함
  const isEventTermsAgreed =
    productId !== THIRD_PURCHASE_ID ||
    (productId === THIRD_PURCHASE_ID &&
      checkTerms.find(({ id }) => id === THIRD_PURCHASE_EVENT_TERMS_ID_1)?.checked &&
      checkTerms.find(({ id }) => id === THIRD_PURCHASE_EVENT_TERMS_ID_2)?.checked)

  const isTestEventTermsAgreed =
    productId !== TEST_THIRD_PURCHASE_ID ||
    (productId === TEST_THIRD_PURCHASE_ID &&
      checkTerms.find(({ id }) => id === TEST_THIRD_PURCHASE_EVENT_TERMS_ID_1)?.checked &&
      checkTerms.find(({ id }) => id === TEST_THIRD_PURCHASE_EVENT_TERMS_ID_2)?.checked)

  const isThirdEventAgreed =
    productId === THIRD_PURCHASE_ID
      ? isEventTermsAgreed
      : productId === TEST_THIRD_PURCHASE_ID
        ? isTestEventTermsAgreed
        : false

  // 청약 필수 약관 상세 보기 modal
  const openTermsModal = () => {
    if (typeof termsId !== 'number') return

    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => (
        <TermsDetailModal
          termsId={termsId}
          isScrollEvent={true}
          buttonProps={{
            footerContent: '해당 내용을 확인했으며 동의해요',
            type: 'primary',
          }}
          onDismiss={(result) => {
            setTermsId(undefined)
            closeModal(MODALS_ID.TERMS_DETAIL)
            if (!result) return

            const isRequired = terms?.find(({ id }) => id === termsId)?.required ?? false
            setCheckTerms((prevState) =>
              prevState.map((v) =>
                v.id === termsId ? { id: Number(termsId), checked: true, required: isRequired } : v,
              ),
            )
          }}
        />
      ),
    })
  }

  // 청약 선택 약관 상세 보기 modal
  const openOptionalTermsModal = (id: number) => {
    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => (
        <TermsDetailModal
          termsId={id}
          buttonProps={{ type: 'primary' }}
          onDismiss={() => {
            closeModal(MODALS_ID.TERMS_DETAIL)
          }}
        />
      ),
    })
  }

  // 청약 api 호출 + 로딩 modal
  const openOrderLoadingModal = () => {
    openModal({
      id: MODALS_ID.ORDER_LOADING,
      Component: () => (
        <OrderLoadingModal
          saleData={{
            id: productId,
            count: amountValue ?? 0,
            terms: checkTerms.map(({ id, checked }) => ({ termsId: id, approved: checked })),
          }}
          success={() => navigate(-1)}
          onDismiss={() => closeModal(MODALS_ID.ORDER_LOADING)}
        />
      ),
    })
  }

  // 3호 청약 이벤트 유도 안내 modal
  const openEventTermsNoticeModal = () => {
    openModal({
      id: MODALS_ID.ORDER_EVENT_NOTICE,
      Component: () => <EventTermsNoticeModal order={openOrderLoadingModal} />,
    })
  }

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  // 청약 신청하기 상태가 아니면 에러
  useEffect(() => {
    if (productDetail) {
      abrStartSubscriptionProduct(productDetail)
      if (productDetail.subscriptionStatus !== 'SUBSCRIPTION_AVAILABLE') {
        setInvalidAccess(true)
      }
    }
  }, [productDetail])

  // 약관 초기값 셋팅
  useEffect(() => {
    if (!terms) return
    setCheckTerms([...terms.map(({ id, required }) => ({ id, required, checked: false }))])
  }, [terms])

  useEffect(() => {
    if (typeof termsId === 'undefined') return
    openTermsModal()
  }, [termsId])

  // status 관리
  useEffect(() => {
    const total = offerPrice * (amountValue ?? 0)
    if (!amountValue) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.INPUT)
    } else if (residualDeposit < 0 || total > Number(balance)) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.BALANCE)
    } else if (
      resultAmount > LIMIT_PRICE ||
      resultAmount > (productDetail?.activeProductSale?.subscriptionLimitPrice ?? 0)
    ) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.OVER_AMOUNT)
    } else if (!checkTerms.length || !checkTerms.filter(({ required }) => required).every((v) => v.checked)) {
      setValidateStatus(SUBSCRIPTION_VALIDATE.TERMS)
    } else {
      setValidateStatus(SUBSCRIPTION_VALIDATE.OKAY)
    }
  }, [amountValue, checkTerms])

  useEffect(() => {
    setTextWidth(amountValue ? inputTextRef.current?.clientWidth : 0)
  }, [amountValue, inputTextRef])

  if (isPending || isError || isDepositAccountPending || isDepositAccountError || isTermsPending || isTermsError) {
    return (
      <Wrap>
        <Header title="청약 신청하기" type="SUB" />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  // input값 수정
  const handleAmount = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (Number(value) < 0 || value.includes('.') || value.includes('-') || Number(value) > LIMIT_PRICE) return
    setAmountValue(Number(value))
  }

  // 청약 수량 설정
  const incrementAmount = ({ currentTarget: { name } }: MouseEvent<HTMLButtonElement>) => {
    switch (name) {
      case '10':
        setAmountValue((prevState) => (prevState || 0) + 10)
        return
      case '50':
        setAmountValue((prevState) => (prevState || 0) + 50)
        return
      case '100':
        setAmountValue((prevState) => (prevState || 0) + 100)
        return
      case 'max':
        setAmountValue(maximumCount())
        return
      default:
        return
    }
  }

  // 최대 신청 가능 수량
  const maximumCount = () => {
    if (!productDetail || !productDetail.activeProductSale) return 0

    const {
      remainCount, // 잔여 갯수
      offerPrice, // 개당 가격
      subscriptionLimitPrice, // 청약 한도 금액
    } = productDetail.activeProductSale

    const limit = Math.floor(subscriptionLimitPrice / offerPrice)

    return limit >= remainCount ? limit : remainCount
  }

  return (
    <Wrap>
      <Header title="청약 신청하기" type="SUB" />
      <StyledContent backgroundColor="gray_1">
        {/* 청약 정보 */}
        <ProductInfoContainer>
          <ProductInfoCard>
            <Text size={18} weight={600}>
              청약 정보
            </Text>
            <Text size={16} color="text_secondary">
              {productDetail.artist} • {productDetail.name}
            </Text>
            <RowBetween>
              <Text size={16} color="text_secondary">
                모집총액
              </Text>
              <Text size={16} weight={500}>
                {productDetail.activeProductSale?.totalIssuedPrice.toLocaleString()}원
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={16} color="text_secondary">
                모집가액
              </Text>
              <Text size={16} weight={500}>
                {offerPrice.toLocaleString()}원
              </Text>
            </RowBetween>
          </ProductInfoCard>
        </ProductInfoContainer>
        {/* 구매 수량 입력 및 약관 동의 */}
        <OrderInfoContainer>
          <AmountInputContainer textWidth={textWidth}>
            <span ref={inputTextRef}>{amountValue}</span>
            <AmountInput
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={amountValue || ''}
              placeholder="수량을 입력해주세요."
              onChange={handleAmount}
              onKeyDown={(e) => {
                const notNumberArr = ['.', '-', '+', '*', '#']
                const isNotNumber = notNumberArr.includes(e.key)
                if (isNotNumber) e.preventDefault()
              }}
            />
            <AmountInputActiveBorder />
          </AmountInputContainer>
          <AmountButtonBox>
            <AmountButton name="10" onClick={incrementAmount}>
              +10주
            </AmountButton>
            <AmountButton name="50" onClick={incrementAmount}>
              +50주
            </AmountButton>
            <AmountButton name="100" onClick={incrementAmount}>
              +100주
            </AmountButton>
            <AmountButton name="max" onClick={incrementAmount}>
              최대 수량
            </AmountButton>
          </AmountButtonBox>
          <CostContainer>
            <ResultAmountBox>
              <RowBetween>
                <Text size={18} weight={600}>
                  총 신청 금액
                </Text>
                <Text size={18} weight={600} color="primary_5">
                  {resultAmount.toLocaleString()}원
                </Text>
              </RowBetween>
              {residualDeposit < 0 ? (
                <Text size={12} color="error">
                  보유한 {SUBSCRIPTION_VALIDATE_MESSAGE[SUBSCRIPTION_VALIDATE.BALANCE]}
                </Text>
              ) : (
                (resultAmount > LIMIT_PRICE ||
                  resultAmount > (productDetail.activeProductSale?.subscriptionLimitPrice ?? 0)) && (
                  <Text size={12} color="error">
                    {SUBSCRIPTION_VALIDATE_MESSAGE[SUBSCRIPTION_VALIDATE.OVER_AMOUNT]}
                  </Text>
                )
              )}
            </ResultAmountBox>
            <RowBetween>
              <Text size={16} color="text_secondary">
                보유 예치금
              </Text>
              <Text size={16} weight={500}>
                {balance?.toLocaleString()}원
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={16} color="text_secondary">
                잔여 예치금
              </Text>
              <Text size={16} weight={500}>
                {residualDeposit < 0 ? 0 : residualDeposit.toLocaleString()}원
              </Text>
            </RowBetween>
          </CostContainer>
          <DivideLine />
          <TermsContainer>
            {terms
              ?.filter(
                ({ id }) =>
                  id !== THIRD_PURCHASE_EVENT_TERMS_ID_1 &&
                  id !== THIRD_PURCHASE_EVENT_TERMS_ID_2 &&
                  id !== TEST_THIRD_PURCHASE_EVENT_TERMS_ID_1 &&
                  id !== TEST_THIRD_PURCHASE_EVENT_TERMS_ID_2,
              )
              .map(({ id, name, required }) => {
                const term = checkTerms.find((term) => term.id === id)
                const isChecked = term?.checked ?? false

                return (
                  <TermsItem
                    key={id}
                    onClick={() =>
                      isChecked
                        ? setCheckTerms((prevState) =>
                            prevState.map((terms) => (terms.id === id ? { ...terms, checked: false } : terms)),
                          )
                        : setTermsId(id)
                    }
                  >
                    {isChecked ? <CheckedIcon /> : <UncheckedIcon />}
                    <Text size={14} color={isChecked ? 'text_primary' : 'text_secondary'}>
                      ({required ? '필수' : '선택'}) {name}
                    </Text>
                    <ArrowIcon />
                  </TermsItem>
                )
              })}
          </TermsContainer>
          {/* 3호 청약 이벤트 약관 관련 */}
          {productId === THIRD_PURCHASE_ID ? (
            <EventTerms>
              {terms
                ?.filter(({ id }) => id === THIRD_PURCHASE_EVENT_TERMS_ID_1 || id === THIRD_PURCHASE_EVENT_TERMS_ID_2)
                .map(({ id, name, required }) => {
                  const term = checkTerms.find((term) => term.id === id)
                  const isChecked = term?.checked ?? false

                  const toggleCheck = () =>
                    setCheckTerms((prevState) =>
                      prevState.map((terms) => (terms.id === id ? { ...terms, checked: !isChecked } : terms)),
                    )

                  const openTermsDetail = (e: MouseEvent<HTMLSpanElement>) => {
                    e.stopPropagation()
                    openOptionalTermsModal(id)
                  }

                  return (
                    <EventTermsItem key={id} onClick={toggleCheck}>
                      <AlignCenterItem $gap={8}>
                        {isChecked ? <CheckedIcon /> : <UncheckedIcon />}
                        <Text size={14} color={isChecked ? 'text_primary' : 'text_secondary'}>
                          ({required ? '필수' : '선택'}) {name}
                        </Text>
                      </AlignCenterItem>
                      <Text
                        size={12}
                        color="text_secondary"
                        $textDecoration="underline"
                        onClick={openTermsDetail}
                        style={{ flexShrink: 0 }}
                      >
                        보기
                      </Text>
                    </EventTermsItem>
                  )
                })}
            </EventTerms>
          ) : null}
          {/* 251210 3호 청약 테스트 */}
          {productId === TEST_THIRD_PURCHASE_ID ? (
            <EventTerms>
              {terms
                ?.filter(
                  ({ id }) =>
                    id === TEST_THIRD_PURCHASE_EVENT_TERMS_ID_1 || id === TEST_THIRD_PURCHASE_EVENT_TERMS_ID_2,
                )
                .map(({ id, name, required }) => {
                  const term = checkTerms.find((term) => term.id === id)
                  const isChecked = term?.checked ?? false

                  const toggleCheck = () =>
                    setCheckTerms((prevState) =>
                      prevState.map((terms) => (terms.id === id ? { ...terms, checked: !isChecked } : terms)),
                    )

                  const openTermsDetail = (e: MouseEvent<HTMLSpanElement>) => {
                    e.stopPropagation()
                    openOptionalTermsModal(id)
                  }

                  return (
                    <EventTermsItem key={id} onClick={toggleCheck}>
                      <AlignCenterItem $gap={8}>
                        {isChecked ? <CheckedIcon /> : <UncheckedIcon />}
                        <Text size={14} color={isChecked ? 'text_primary' : 'text_secondary'}>
                          ({required ? '필수' : '선택'}) {name}
                        </Text>
                      </AlignCenterItem>
                      <Text
                        size={12}
                        color="text_secondary"
                        $textDecoration="underline"
                        onClick={openTermsDetail}
                        style={{ flexShrink: 0 }}
                      >
                        보기
                      </Text>
                    </EventTermsItem>
                  )
                })}
            </EventTerms>
          ) : null}
        </OrderInfoContainer>
        <NoticeContainer
          title="청약 주의 사항"
          list={[
            '청약 취소는 청약 기간 내에만 가능해요.',
            '최대 청약 신청 금액은 3,000만 원을 초과할 수 없어요.',
            '모집에 대한 청약 경쟁률, 청약 참여자 수 등에 따라 청약을 신청한 증권 수 보다 적은 수의 증권을 배정받을 수 있어요. 이때, 잔여 청약 증거금은 투자자의 NH 농협 예치금 계좌로 환불 처리됩니다.',
          ]}
        />
      </StyledContent>
      <Footer>
        <Button
          type={SUBSCRIPTION_VALIDATE.OKAY === validateStatus ? 'primary' : 'default'}
          size="large"
          disabled={SUBSCRIPTION_VALIDATE.OKAY !== validateStatus}
          block={true}
          {...(SUBSCRIPTION_VALIDATE.OKAY
            ? {
                onClick: isThirdEventAgreed ? openOrderLoadingModal : openEventTermsNoticeModal,
              }
            : undefined)}
          // {...(VALIDATE_STATUS_TRADE.OKAY === validateStatus ? LoadingModal : undefined)}
        >
          {SUBSCRIPTION_VALIDATE_MESSAGE[validateStatus]}
        </Button>
      </Footer>
    </Wrap>
  )
}
